/**
 * Paginação por chave (keyset) para as listas grandes do PostgREST.
 *
 * A Data API corta a resposta em 1.000 linhas, e o padrão antigo do projeto era
 * `.range(from, from + 999)` — que vira `LIMIT 1000 OFFSET n` no Postgres. O
 * problema é que OFFSET não pula linhas: ele lê e **descarta**. Medido em
 * produção na tabela `faturas` (7.8k linhas, 184 colunas), a mesma página custa
 *
 *     OFFSET 7000  →  1.225 ms   (Index Scan materializa 7.820 linhas)
 *     id > 8000    →     43 ms   (Index Cond corta no índice)
 *
 * Como a tela varre a tabela inteira, o custo do OFFSET é quadrático: a 8ª
 * página relê tudo que as sete anteriores já tinham lido. Era isso que estourava
 * o `statement_timeout` de 8s do papel `authenticated` e devolvia 500 para a
 * lista de faturas.
 *
 * A paginação por chave usa a última chave lida como corte (`id > ultimo`), então
 * cada página custa o mesmo, independente de quão fundo está. Continua sendo uma
 * página de cada vez — é o preço de depender do último id —, mas cada ida ao
 * banco fica ~28x mais barata, o que já compensa com folga.
 *
 * Requisitos de quem chama: ordenar pela MESMA coluna do corte, em ordem
 * crescente, e essa coluna precisa ser única (chave primária serve).
 */

const TAMANHO_PAGINA = 1000;

/** Recebe a última chave lida (null na primeira página) e devolve a query da
 *  página. O builder do supabase-js é mutável, então cada página precisa montar
 *  o seu — reaproveitar um builder entre páginas embaralha os filtros. */
type ConstrutorPagina<T> = (aposChave: string | number | null, tamanho: number) => PromiseLike<{
  data: T[] | null;
  error: { message: string } | null;
}>;

/**
 * Varre a tabela inteira paginando pela chave.
 *
 * `construir` deve aplicar `.gt(<chave>, aposChave)` quando `aposChave` não for
 * nulo, ordenar por essa mesma coluna em ordem crescente e usar `.limit(tamanho)`.
 * `extrairChave` diz qual campo da linha é a chave do corte.
 *
 * O teto de páginas é uma trava contra laço infinito: se `extrairChave` apontar
 * para uma coluna não-única, o cursor pode parar de avançar.
 */
export async function buscarTudoPorChave<T>(
  construir: ConstrutorPagina<T>,
  extrairChave: (linha: T) => string | number,
  tamanhoPagina = TAMANHO_PAGINA,
  maxPaginas = 200,
): Promise<T[]> {
  const todas: T[] = [];
  let cursor: string | number | null = null;

  for (let pagina = 0; pagina < maxPaginas; pagina++) {
    const { data, error } = await construir(cursor, tamanhoPagina);
    if (error) throw error;

    const linhas = data || [];
    if (linhas.length === 0) break;

    todas.push(...linhas);
    if (linhas.length < tamanhoPagina) break;

    const proximo = extrairChave(linhas[linhas.length - 1]);
    if (proximo == null || proximo === cursor) break;
    cursor = proximo;
  }

  return todas;
}
